import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

const EXPAND_ANIMATION_MS = 200;
// Fallback if `transitionend` never fires (e.g. toggled before the animation begins).
// Slightly longer than the CSS transition.
const EXPAND_ANIMATION_TIMEOUT_MS = 260;

interface WeekExpandableRowProps {
  /** Whether the row is expanded. */
  isExpanded: boolean;
  /**
   * Content rendered inside the expanding panel. Receives `hasOpened` so callers
   * can defer building expensive content (or fetching) until the first open —
   * the inner content always mounts wrapped, so the open animation can play
   * while data loads.
   */
  children: (hasOpened: boolean) => ReactNode;
}

/**
 * Full-width expandable row for the week grid that drives a height transition
 * deterministically so the animation runs exactly once per toggle. The content
 * mounts the moment the user opens the row for the first time so the open
 * animation can play while any data loads in the background.
 *
 * Sequence:
 *   - opening: 0 → measured px (animate) → 'auto' on transitionend
 *   - closing: 'auto' → measured px (no animation) → 0 (animate) on next frame
 *
 * We avoid `grid-template-rows` because the row lives inside the outer
 * `.tt-week-grid` CSS grid; animating that grid's row template produced a
 * double-collapse (one for the inner transition, one for the outer grid
 * reflowing the auto-sized row track after the transition ended).
 *
 * Using `useLayoutEffect` guarantees the initial "pin to numeric px" frame is
 * committed before the next state update flips to the target — React 18 batching
 * otherwise collapses both updates into a single render and the transition is
 * skipped (which caused a previous "collapse → flash open → collapse" artifact).
 *
 * After the open transition completes, the wrapper is released to `auto` so any
 * nested collapsible (e.g. a DayGroup) can shrink/grow it in sync, instead of
 * staying pinned to its first-open height (which used to cause a second "snap"
 * when the nested collapse finished). The release uses `transitionend` with a
 * timeout fallback so dependent toggles done mid-open still finish cleanly.
 */
export function WeekExpandableRow({ isExpanded, children }: WeekExpandableRowProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Mount the inner content immediately on first open so the animation has
  // measurable content even before any backend data lands.
  const [hasOpened, setHasOpened] = useState(isExpanded);
  const [height, setHeight] = useState<number | 'auto'>(isExpanded ? 'auto' : 0);

  // First-mount step: as soon as the user opens the row, mount the inner
  // content. Separate effect so the DOM has the new content committed before
  // the measurement effect below reads scrollHeight.
  useLayoutEffect(() => {
    if (isExpanded && !hasOpened) setHasOpened(true);
  }, [isExpanded, hasOpened]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    if (isExpanded) {
      const target = content.scrollHeight;
      // If the content isn't mounted yet (very first open), scrollHeight is 0.
      // Skip this run; the hasOpened effect above triggers a re-render with
      // content mounted, and this effect re-runs with a real target.
      if (target === 0 && !hasOpened) return;
      setHeight(target);

      const release = () => setHeight('auto');
      const onEnd = (event: TransitionEvent) => {
        // Ignore events that bubble up from nested transitionable children
        // (e.g. an inner DayGroup height collapse) — only release when the
        // wrapper's own height transition finishes.
        if (event.target !== wrapper || event.propertyName !== 'height') return;
        release();
      };
      wrapper.addEventListener('transitionend', onEnd);
      const fallback = window.setTimeout(release, EXPAND_ANIMATION_TIMEOUT_MS);
      return () => {
        wrapper.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
      };
    }

    // Closing: pin to the current measured height first, then flip to 0 on the
    // next frame so the transition has a numeric starting point. Double rAF
    // guarantees the browser paints the pinned height before the 0.
    const current = content.scrollHeight;
    setHeight(current);
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setHeight(0));
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isExpanded, hasOpened]);

  return (
    <div
      ref={wrapperRef}
      style={{
        gridColumn: '1 / -1',
        height: height === 'auto' ? 'auto' : `${height}px`,
        overflow: 'hidden',
        transition: `height ${EXPAND_ANIMATION_MS}ms ease-out`,
      }}
    >
      <div ref={contentRef}>{children(hasOpened)}</div>
    </div>
  );
}
